import {
  BookOpen, FileUp, Layers, Lightbulb, Lock, Map, Mic, PenTool, Sparkles, Star, Wand2, Zap
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";

const ModeSelector = ({
  setMode,
  showDraftPicker,
  setShowDraftPicker,
  availableDrafts,
  setAvailableDrafts,
  setBlueprintData,
  setSituations,
  setStep,
  setHasUnsavedChanges,
  freebumMode = false,
}) => {
  const handleJourneyClick = () => {
    const _uid = JSON.parse(localStorage.getItem('shweebum_user') || '{}').id || '';
    const _journeyKey = _uid ? `shweebum_journey_drafts_${_uid}` : 'shweebum_journey_drafts';
    const draftsJson = localStorage.getItem(_journeyKey);
    if (draftsJson) {
      try {
        const drafts = JSON.parse(draftsJson);
        const draftList = Object.values(drafts);
        if (draftList.length > 0) {
          setAvailableDrafts(draftList);
          setShowDraftPicker(true);
          return;
        }
      } catch (e) {
        console.error("Failed to parse drafts:", e);
      }
    }
    setMode("journey");
  };

  const handleLoadDraft = (draft) => {
    if (draft.blueprintData) setBlueprintData(draft.blueprintData);
    if (draft.situations) setSituations(draft.situations);
    setMode("journey");
    setStep(2);
    setShowDraftPicker(false);
    setHasUnsavedChanges(false);
  };

  const handleDeleteDraft = (draftTitle) => {
    try {
      const _uid2 = JSON.parse(localStorage.getItem('shweebum_user') || '{}').id || '';
      const _journeyKey2 = _uid2 ? `shweebum_journey_drafts_${_uid2}` : 'shweebum_journey_drafts';
      const drafts = JSON.parse(localStorage.getItem(_journeyKey2) || '{}');
      const draftKey = draftTitle.toLowerCase().trim();
      delete drafts[draftKey];
      localStorage.setItem(_journeyKey2, JSON.stringify(drafts));
      const remaining = Object.values(drafts);
      setAvailableDrafts(remaining);
      if (remaining.length === 0) setShowDraftPicker(false);
    } catch (e) {
      console.error("Failed to delete draft:", e);
    }
  };

  if (showDraftPicker) {
    return (
      <div className="space-y-4">
        <div className="text-center mb-4">
          <h3 className="font-semibold text-lg text-foreground">You have saved drafts!</h3>
          <p className="text-sm text-muted-foreground">Continue where you left off, or start fresh</p>
        </div>
        <div className="space-y-3">
          {availableDrafts.map((draft, idx) => (
            <Card key={idx} className="cursor-pointer hover:border-indigo-400 transition-all">
              <CardContent className="p-4 flex items-center justify-between">
                <div onClick={() => handleLoadDraft(draft)} className="flex-1">
                  <h4 className="font-semibold">{draft.title}</h4>
                  <p className="text-xs text-muted-foreground">
                    Saved {new Date(draft.savedAt).toLocaleString()} 
                    {draft.situations && ` - ${draft.situations.filter(s => s.description?.trim()).length} situations`}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-700 ml-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteDraft(draft.title);
                  }}
                >
                  Delete
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={() => setShowDraftPicker(false)}>
            Back
          </Button>
          <Button onClick={() => { setShowDraftPicker(false); setMode("journey"); }}>
            Start Fresh
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 py-2 overflow-y-auto max-h-[calc(90vh-120px)]">
      <div className="text-center mb-2">
        <p className="text-muted-foreground text-sm">How would you like to start?</p>
        {freebumMode ? (
          <p className="text-xs text-cyan-500 mt-1">Free writing tools — your story, your way!</p>
        ) : (
          <p className="text-xs text-purple-500 mt-1">Your story. Your vision. I'm just here to help bring it to life.</p>
        )}
      </div>
      
      {freebumMode ? (
        /* FREEBUM SIMPLIFIED MODES */
        <div className="grid grid-cols-1 gap-3 pb-4">
          {/* Simple Manual Writing */}
          <Card 
            className="cursor-pointer btn-pop border border-cyan-500/40 bg-gradient-to-br from-cyan-950/60 to-blue-950/30 hover:border-cyan-400/70 hover:shadow-lg hover:shadow-cyan-500/10 transition-all duration-300"
            onClick={() => setMode("manual")}
            data-testid="freebum-manual-mode-btn"
          >
            <CardContent className="py-4 px-4 text-center">
              <div className="w-10 h-10 mx-auto rounded-xl bg-cyan-500/20 flex items-center justify-center mb-2">
                <PenTool className="w-5 h-5 text-cyan-400" />
              </div>
              <h3 className="font-bold text-sm text-cyan-50">Start Writing</h3>
              <Badge className="bg-cyan-500 text-white text-[10px] px-2 py-0 mt-1">FREE</Badge>
              <p className="text-xs text-cyan-200/60 mt-1.5">
                Jump right in — create a blank book and write your story freely.
              </p>
            </CardContent>
          </Card>

          {/* Simple Story Builder */}
          <Card 
            className="cursor-pointer btn-pop border border-emerald-500/40 bg-gradient-to-br from-emerald-950/60 to-emerald-900/20 hover:border-emerald-400/70 hover:shadow-lg hover:shadow-emerald-500/10 transition-all duration-300"
            onClick={() => setMode("builder")}
            data-testid="freebum-builder-mode-btn"
          >
            <CardContent className="py-4 px-4 text-center">
              <div className="w-10 h-10 mx-auto rounded-xl bg-emerald-500/20 flex items-center justify-center mb-2">
                <Wand2 className="w-5 h-5 text-emerald-400" />
              </div>
              <h3 className="font-bold text-sm text-emerald-50">Story Builder</h3>
              <Badge className="bg-emerald-500 text-white text-[10px] px-2 py-0 mt-1">GUIDED</Badge>
              <p className="text-xs text-emerald-200/60 mt-1.5">
                Describe your idea — get help building your outline and characters.
              </p>
            </CardContent>
          </Card>

          {/* Upgrade CTA - Links to pricing */}
          <Link to="/pricing" className="block" data-testid="freebum-upgrade-cta">
            <div className="text-center p-4 bg-gradient-to-r from-violet-950/40 to-purple-950/40 rounded-xl border border-violet-500/20 cursor-pointer hover:border-violet-400/40 hover:shadow-lg hover:shadow-violet-500/10 transition-all duration-300">
              <Sparkles className="w-6 h-6 text-violet-400 mx-auto mb-2" />
              <p className="text-sm text-violet-200">Want more options?</p>
              <p className="text-xs text-violet-300/60 mt-1">
                Quick Story, Voice Mode, Verbatim Mode & more with Plus!
              </p>
            </div>
          </Link>
        </div>
      ) : (
        /* FULL MODES for paid users */
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-4">
        
        {/* 1. QUICK STORY */}
        <Card 
          className="cursor-pointer btn-pop border border-emerald-500/40 bg-gradient-to-br from-emerald-950/60 to-emerald-900/20 hover:border-emerald-400/70 hover:shadow-lg hover:shadow-emerald-500/10 transition-all duration-300"
          onClick={() => setMode("quick")}
          data-testid="quick-mode-btn"
        >
          <CardContent className="py-4 px-4 text-center">
            <div className="w-10 h-10 mx-auto rounded-xl bg-emerald-500/20 flex items-center justify-center mb-2">
              <Zap className="w-5 h-5 text-emerald-400" />
            </div>
            <h3 className="font-bold text-sm text-emerald-50">Quick Story</h3>
            <Badge className="bg-emerald-500 text-white text-[10px] px-2 py-0 mt-1">INSTANT</Badge>
            <p className="text-xs text-emerald-200/60 mt-1.5">
              One click — get a complete 1,000-2,000 word story in minutes.
            </p>
          </CardContent>
        </Card>

        {/* 2. STORY BUILDER */}
        <Card 
          className="cursor-pointer btn-pop border border-cyan-500/40 bg-gradient-to-br from-cyan-950/60 to-blue-950/30 hover:border-cyan-400/70 hover:shadow-lg hover:shadow-cyan-500/10 transition-all duration-300"
          onClick={() => setMode("builder")}
          data-testid="builder-mode-btn"
        >
          <CardContent className="py-4 px-4 text-center">
            <div className="w-10 h-10 mx-auto rounded-xl bg-cyan-500/20 flex items-center justify-center mb-2">
              <Wand2 className="w-5 h-5 text-cyan-400" />
            </div>
            <h3 className="font-bold text-sm text-cyan-50">Story Builder</h3>
            <Badge className="bg-cyan-500 text-white text-[10px] px-2 py-0 mt-1">GUIDED</Badge>
            <p className="text-xs text-cyan-200/60 mt-1.5">
              Describe your idea — AI builds characters, world, and chapters for you.
            </p>
          </CardContent>
        </Card>

        {/* 3. UPLOAD YOUR BOOK — VERBATIM MODE */}
        <Card 
          className="cursor-pointer btn-pop relative overflow-hidden border-2 border-pink-400/60 bg-gradient-to-br from-pink-950/70 via-fuchsia-950/50 to-purple-950/40 hover:border-pink-300 hover:shadow-xl hover:shadow-pink-500/30 transition-all duration-300 sm:col-span-2 ring-2 ring-pink-500/20"
          onClick={() => setMode("upload")}
          data-testid="upload-mode-btn"
        >
          {/* Animated stars background */}
          <div className="absolute inset-0 overflow-hidden">
            <Star className="absolute top-2 right-3 w-3 h-3 text-pink-300/40 animate-pulse" />
            <Star className="absolute top-4 right-8 w-2 h-2 text-fuchsia-300/30 animate-pulse" style={{animationDelay: '0.5s'}} />
            <Star className="absolute bottom-3 left-4 w-2.5 h-2.5 text-pink-200/35 animate-pulse" style={{animationDelay: '1s'}} />
            <Star className="absolute top-1/2 right-4 w-2 h-2 text-purple-300/25 animate-pulse" style={{animationDelay: '0.3s'}} />
            <Lock className="absolute bottom-2 right-6 w-4 h-4 text-pink-400/30" />
          </div>
          <CardContent className="py-5 px-4 text-center relative z-10">
            <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-pink-500/40 to-fuchsia-600/40 flex items-center justify-center mb-2 border border-pink-400/30 shadow-lg shadow-pink-500/20">
              <FileUp className="w-6 h-6 text-pink-200" />
            </div>
            <h3 className="font-bold text-base text-pink-50 drop-shadow-sm">Verbatim Mode</h3>
            <Badge className="bg-gradient-to-r from-pink-500 to-fuchsia-500 text-white text-[10px] px-3 py-0.5 mt-1.5 shadow-sm">YOUR STORY, EXACTLY</Badge>
            <p className="text-xs text-pink-100/70 mt-2 leading-relaxed">
              Upload your content — AI follows your plot, characters, and locations EXACTLY as written.
            </p>
            <p className="text-[10px] text-pink-300/50 mt-1 italic">
              No changes to your vision
            </p>
          </CardContent>
        </Card>

        {/* 4. I HAVE AN IDEA — CREATIVE MODE */}
        <Card 
          className="cursor-pointer btn-pop relative overflow-hidden border-2 border-cyan-500/30 bg-gradient-to-br from-slate-800/50 via-slate-800/40 to-slate-700/30 hover:border-cyan-400/80 hover:shadow-lg hover:shadow-cyan-500/20 transition-all duration-300"
          onClick={() => setMode("idea")}
          data-testid="idea-mode-btn"
        >
          {/* Creative sparks decoration */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <Sparkles className="absolute top-2 right-3 w-4 h-4 text-orange-400/50 animate-pulse" />
            <Zap className="absolute top-4 left-3 w-3 h-3 text-yellow-400/40 animate-pulse" style={{animationDelay: '0.3s'}} />
            <Star className="absolute bottom-3 right-5 w-3 h-3 text-cyan-300/40 animate-pulse" style={{animationDelay: '0.6s'}} />
          </div>
          <CardContent className="py-4 px-4 text-center relative z-10">
            <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-orange-500/30 to-yellow-500/30 flex items-center justify-center mb-2 border border-orange-400/40 shadow-lg shadow-orange-500/20">
              <Lightbulb className="w-6 h-6 text-cyan-300" />
            </div>
            <h3 className="font-bold text-base text-orange-50">Creative Mode</h3>
            <Badge className="bg-gradient-to-r from-orange-500 to-blue-500 text-white text-[10px] px-3 py-0.5 mt-1 shadow-sm">AI INVENTS</Badge>
            <p className="text-xs text-orange-200/70 mt-2 leading-relaxed">
              Give me an idea — I'll invent characters, plot twists, and build your entire story!
            </p>
            <p className="text-[10px] text-cyan-300/50 mt-1 italic">
              Full creative freedom
            </p>
          </CardContent>
        </Card>

        {/* 5. KEY MOMENTS */}
        <Card 
          className="cursor-pointer btn-pop border border-indigo-500/40 bg-gradient-to-br from-indigo-950/60 to-violet-950/20 hover:border-indigo-400/70 hover:shadow-lg hover:shadow-indigo-500/10 transition-all duration-300"
          onClick={handleJourneyClick}
          data-testid="journey-mode-btn"
        >
          <CardContent className="py-4 px-4 text-center">
            <div className="w-10 h-10 mx-auto rounded-xl bg-indigo-500/20 flex items-center justify-center mb-2">
              <Map className="w-5 h-5 text-indigo-400" />
            </div>
            <h3 className="font-bold text-sm text-indigo-50">I Know Key Moments</h3>
            <Badge className="bg-indigo-500 text-white text-[10px] px-2 py-0 mt-1">BLUEPRINT</Badge>
            <p className="text-xs text-indigo-200/60 mt-1.5">
              Tell me your pivotal scenes — I'll weave them into chapters.
            </p>
          </CardContent>
        </Card>

        {/* 6. VOICE TO STORY */}
        <Card 
          className="cursor-pointer btn-pop border border-purple-500/40 bg-gradient-to-br from-purple-950/60 to-fuchsia-950/20 hover:border-purple-400/70 hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-300"
          onClick={() => setMode("voice")}
          data-testid="voice-mode-btn"
        >
          <CardContent className="py-4 px-4 text-center">
            <div className="w-10 h-10 mx-auto rounded-xl bg-purple-500/20 flex items-center justify-center mb-2">
              <Mic className="w-5 h-5 text-purple-400" />
            </div>
            <h3 className="font-bold text-sm text-purple-50">Tell Me Your Story</h3>
            <Badge className="bg-purple-500 text-white text-[10px] px-2 py-0 mt-1">VOICE</Badge>
            <p className="text-xs text-purple-200/60 mt-1.5">
              Just speak — I'll turn your words into a story.
            </p>
          </CardContent>
        </Card>

        {/* 7. GENRE BLUEPRINT */}
        <Card 
          className="cursor-pointer btn-pop relative overflow-hidden border-2 border-teal-500/50 bg-gradient-to-br from-teal-950/60 via-cyan-950/40 to-emerald-950/30 hover:border-teal-400/80 hover:shadow-lg hover:shadow-teal-500/20 transition-all duration-300"
          onClick={() => setMode("genre_template")}
          data-testid="genre-template-mode-btn"
        >
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <Layers className="absolute top-2 right-3 w-4 h-4 text-teal-400/40 animate-pulse" />
            <BookOpen className="absolute bottom-3 left-3 w-3 h-3 text-emerald-400/30 animate-pulse" style={{animationDelay: '0.5s'}} />
          </div>
          <CardContent className="py-4 px-4 text-center relative z-10">
            <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-teal-500/30 to-emerald-500/30 flex items-center justify-center mb-2 border border-teal-400/40 shadow-lg shadow-teal-500/20">
              <Layers className="w-6 h-6 text-teal-300" />
            </div>
            <h3 className="font-bold text-base text-teal-50">Genre Blueprint</h3>
            <Badge className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-[10px] px-3 py-0.5 mt-1 shadow-sm">15+ GENRES</Badge>
            <p className="text-xs text-teal-200/70 mt-2 leading-relaxed">
              Pick a genre — get a pre-built Story Bible, structure, and writing rules instantly.
            </p>
            <p className="text-[10px] text-teal-300/50 mt-1 italic">
              Thriller, Romance, Memoir, Sci-Fi, and more
            </p>
          </CardContent>
        </Card>

        {/* 8. FULL MANUAL */}
        <Card 
          className="cursor-pointer btn-pop border border-emerald-500/30 bg-gradient-to-br from-emerald-950/50 to-teal-950/30 hover:border-emerald-400/50 hover:shadow-lg hover:shadow-emerald-500/10 transition-all duration-300"
          onClick={() => setMode("manual")}
          data-testid="manual-mode-btn"
        >
          <CardContent className="py-4 px-4 text-center">
            <div className="w-10 h-10 mx-auto rounded-xl bg-emerald-500/20 flex items-center justify-center mb-2">
              <PenTool className="w-5 h-5 text-emerald-400" />
            </div>
            <h3 className="font-bold text-sm text-emerald-100">I Know My Story</h3>
            <Badge className="bg-emerald-700 text-white text-[10px] px-2 py-0 mt-1">ADVANCED</Badge>
            <p className="text-xs text-emerald-300/60 mt-1.5">
              Full creative control — set every detail your way.
            </p>
          </CardContent>
        </Card>
      </div>
      )}
    </div>
  );
};

export default ModeSelector;
